<template>
  <b-col>
    <b-row class="justify-content-center">
      <b-col
        cols="12"
        md="8"
        lg="6"
      >
        <!-- Spinner de carga -->
        <b-overlay
            v-if="role != 'super_admin'"
          :show="loading"
          rounded="sm"
        >
          <!-- Tarjeta principal de estado -->
          <b-card
            no-body
            class="journey-card"
          >
            <!-- Header compacto -->
            <div class="journey-header">
              <div class="header-content">
                <div class="avatar-wrapper">
                  <b-avatar
                    v-if="currentUser && currentUser.avatar"
                    :src="currentUser.avatar"
                    class="user-avatar"
                  />
                  <b-avatar
                    v-else
                    variant="light"
                    class="user-avatar avatar-initials"
                  >
                    {{ userInitials }}
                  </b-avatar>
                </div>
                <div class="greeting-wrapper">
                  <h3
                    v-if="currentUser"
                    class="mb-0"
                  >
                    {{ greeting }}, {{ currentUser.name }}
                  </h3>
                  <p class="mb-0">
                    {{ currentDate }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Body compacto -->
            <div class="journey-body">
              <!-- Estado: Jornada finalizada -->
              <div
                v-if="(currentJourney && currentJourney.hour_fin) || (!currentJourney && allJourneys.length > 0)"
                class="status-content"
              >
                <div class="status-icon-inline completed-success">
                  <feather-icon
                    icon="CheckIcon"
                    size="32"
                    class="text-success-strong"
                  />
                </div>
                <h4 class="mb-2">
                  Jornada completada
                </h4>

                <!-- Resumen de todas las jornadas del día -->
                <div class="journeys-summary mb-2">
                  <div
                    v-for="(journey, index) in allJourneys"
                    :key="journey.id"
                    class="journey-summary-box"
                  >
                    <div class="summary-row">
                      <div class="summary-col">
                        <small class="text-muted">Entrada {{ index + 1 }}</small>
                        <p class="mb-0 font-weight-bold">
                          {{ formatTime(journey.hour_ini) }}
                        </p>
                      </div>
                      <div class="summary-col">
                        <small class="text-muted">Salida {{ index + 1 }}</small>
                        <p class="mb-0 font-weight-bold">
                          {{ formatTime(journey.hour_fin) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Total de horas -->
                <div class="total-hours-box mb-2">
                  <p class="text-muted mb-1">
                    Total horas trabajadas
                  </p>
                  <p class="h4 font-weight-bold mb-0">
                    {{ totalHoursAccumulated }}
                  </p>
                </div>

                <b-button
                  variant="primary"
                  block
                  :disabled="loading"
                  @click="reopenJourney"
                >
                  <feather-icon
                    icon="RotateCcwIcon"
                    size="18"
                    class="mr-2"
                  />
                  Reabrir jornada
                </b-button>
              </div>

              <!-- Estado: Jornada iniciada -->
              <div
                v-else-if="currentJourney"
                class="status-content"
              >
                <div class="status-icon-inline active-warning">
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="32"
                    class="text-warning-strong"
                  />
                </div>
                <h4 class="mb-2">
                  Jornada en proceso
                </h4>

                <!-- Info grid -->
                <div class="info-grid mb-2">
                  <div class="info-box">
                    <small class="text-muted">Hora de entrada</small>
                    <p class="mb-0 font-weight-bold">
                      {{ formatTime(currentJourney.hour_ini) }}
                    </p>
                  </div>
                  <div class="info-box">
                    <small class="text-muted">Salida estimada</small>
                    <p class="mb-0 font-weight-bold">
                      {{ currentJourney.hour_config_exit }}
                    </p>
                  </div>
                  <div class="info-box">
                    <small class="text-muted">Horas trabajadas</small>
                    <p class="mb-0 font-weight-bold">
                      {{ calculateAccumulatedActiveTime(currentJourney.hour_ini) }}
                    </p>
                  </div>
                </div>

                <b-button
                  variant="primary"
                  block
                  :disabled="loading"
                  @click="completeJourney"
                >
                  <feather-icon
                    icon="LogOutIcon"
                    size="18"
                    class="mr-2"
                  />
                  Finalizar jornada
                </b-button>
              </div>

              <!-- Estado: Sin jornada iniciada -->
              <div
                v-else
                class="status-content"
              >
                <div class="status-icon-inline pending-info">
                  <feather-icon
                    icon="ClockIcon"
                    size="32"
                    class="text-info-strong"
                  />
                </div>
                <h4 class="mb-2">
                  Sin jornada
                </h4>
                <p class="text-muted mb-2">
                  Inicia tu jornada para comenzar el registro
                </p>

                <b-button
                  variant="primary"
                  block
                  :disabled="loading"
                  @click="startJourney"
                >
                  <feather-icon
                    icon="LogInIcon"
                    size="18"
                    class="mr-2"
                  />
                  Iniciar jornada
                </b-button>
              </div>
            </div>
          </b-card>

          <!-- Tarjeta de información compacta -->
          <b-card
            no-body
            class="info-card mt-2"
          >
            <div class="info-content">
              <div class="info-item">
                <small class="text-muted">ESTADO</small>
                <p class="mt-1 mb-0">
                  <b-badge
                    :variant="getStatusVariant"
                  >
                    {{ getStatusText }}
                  </b-badge>
                </p>
              </div>
              <div class="info-item border-left">
                <small class="text-muted">HORA ACTUAL</small>
                <p class="mt-1 mb-0 font-weight-bold hour-display">
                  {{ currentTime }}
                </p>
              </div>
            </div>
          </b-card>

          <!-- Modal de selección de horario -->
          <b-modal
            id="schedule-modal"
            v-model="showScheduleModal"
            title="Selecciona tu horario"
            ok-title="Iniciar jornada"
            :ok-disabled="!selectedScheduleId"
            :hide-header-close="true"
            no-close-on-backdrop
            no-close-on-esc
            ok-variant="primary"
            cancel-variant="outline-danger"
            cancel-title="Cancelar"
            centered
            @ok="confirmScheduleAndStart"
          >
            <p class="text-muted mb-2">
              Tienes varios horarios disponibles. Selecciona el que corresponde a hoy.
            </p>
            <b-form-group
              label="Horario de trabajo"
              label-for="schedule-modal-select"
            >
              <b-form-select
                id="schedule-modal-select"
                v-model="selectedScheduleId"
                :options="scheduleOptions"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal de motivo del retraso en entrada -->
          <b-modal
            id="motive-start-modal"
            v-model="showMotiveStartModal"
            title="Motivo del Retraso del Fichaje"
            ok-title="Enviar"
            cancel-title="Cancelar"
            ok-variant="primary"
            cancel-variant="outline-primary"
            centered
            @ok="submitStartMotive"
          >
            <b-form-group
              label="Por favor, ingresa el motivo del retraso:"
              label-for="motive-start-input"
            >
              <b-form-input
                id="motive-start-input"
                v-model="motiveStartText"
                type="text"
                placeholder="Describe el motivo del retraso"
                @keydown.enter="submitStartMotive"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal de motivo de la salida -->
          <b-modal
            id="motive-end-modal"
            v-model="showMotiveEndModal"
            title="Motivo de la Finalización"
            ok-title="Enviar"
            cancel-title="Cancelar"
            ok-variant="primary"
            cancel-variant="outline-primary"
            centered
            @ok="submitEndMotive"
          >
            <b-form-group
              label="Por favor, ingresa el motivo de la finalización:"
              label-for="motive-end-input"
            >
              <b-form-input
                id="motive-end-input"
                v-model="motiveEndText"
                type="text"
                placeholder="Describe el motivo de la finalización"
                @keydown.enter="submitEndMotive"
              />
            </b-form-group>
          </b-modal>
        </b-overlay>
      </b-col>
    </b-row>
  </b-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BCol,
  BRow,
  BCard,
  BButton,
  BBadge,
  BOverlay,
  BAvatar,
  BModal,
  BFormGroup,
  BFormInput,
  BFormSelect,
} from 'bootstrap-vue'

export default {
  components: {
    BCol,
    BRow,
    BCard,
    BButton,
    BBadge,
    BOverlay,
    BAvatar,
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
  },
  data() {
    return {
      currentTime: this.getCurrentTime(),
      activeTime: '00:00:00',
      timeInterval: null,
      showMotiveStartModal: false,
      motiveStartText: '',
      showMotiveEndModal: false,
      motiveEndText: '',
      selectedScheduleId: null,
      showScheduleModal: false,
    }
  },
  computed: {
    ...mapGetters({
      currentJourney: 'journeyDay/getCurrentJourney',
      loading: 'journeyDay/getLoading',
      totalHoursAccumulated: 'journeyDay/getTotalHours',
      currentUser: 'auth/getUser',
      role: 'auth/getRole',
      userSchedules: 'journeyDay/getUserSchedules',
    }),
    showScheduleSelector() {
      return this.userSchedules && this.userSchedules.length > 1
    },
    scheduleOptions() {
      return this.userSchedules.map(s => ({ value: s.id, text: s.name }))
    },
    allJourneys() {
      // Esta propiedad se actualiza desde el store cuando se carga la jornada
      return this.$store.state.journeyDay.items || []
    },
    getStatusVariant() {
      // Si hay jornada en progreso = warning (naranja)
      // Si hay jornadas pero ninguna en progreso = success (verde - completada)
      // Si no hay jornadas = info (azul - pendiente)
      if (this.currentJourney) {
        return this.currentJourney.hour_fin ? 'success' : 'warning'
      }
      return this.allJourneys.length > 0 ? 'success' : 'info'
    },
    getStatusText() {
      // Si hay jornada en progreso = En proceso
      // Si hay jornadas pero ninguna en progreso = Completado
      // Si no hay jornadas = Pendiente
      if (this.currentJourney && !this.currentJourney.hour_fin) {
        return 'En proceso'
      }
      return this.allJourneys.length > 0 ? 'Completado' : 'Pendiente'
    },
    userInitials() {
      if (!this.currentUser || !this.currentUser.name) return '?'
      const names = this.currentUser.name.split(' ')
      return names.map(n => n[0]).join('').toUpperCase().slice(0, 2)
    },
    greeting() {
      const hour = new Date().getHours()
      if (hour < 12) return 'Buenos días'
      if (hour < 18) return 'Buenas tardes'
      return 'Buenas noches'
    },
    currentDate() {
      const date = new Date()
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
      const formattedDate = date.toLocaleDateString('es-ES', options)
      const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
      return `Hoy ${capitalizedDate}`
    },
  },
  async mounted() {
    if(this.role != 'super_admin') {
      await Promise.all([this.loadCurrentJourney(), this.fetchUserSchedulesAction()])
      // Actualizar hora y tiempo activo cada segundo
      this.timeInterval = setInterval(() => {
        this.currentTime = this.getCurrentTime()
        if (this.currentJourney && this.currentJourney.hour_ini) {
          // El tiempo activo acumulado se actualiza automáticamente
          // porque calculateAccumulatedActiveTime lo calcula en cada render
          this.$forceUpdate()
        }
      }, 1000)
    }
  },
  beforeDestroy() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }
  },
  methods: {
    ...mapActions({
      startJourneyAction: 'journeyDay/startJourney',
      completeJourneyAction: 'journeyDay/completeJourney',
      getCurrentJourneyAction: 'journeyDay/getCurrentJourney',
      fetchUserSchedulesAction: 'journeyDay/fetchUserSchedules',
    }),
    getCurrentTime() {
      const now = new Date()
      return now.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    formatTime(timestamp) {
      if (!timestamp) return '--:--'
      const date = new Date(timestamp * 1000)
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    calculateAccumulatedActiveTime(timestamp) {
      if (!timestamp) return this.totalHoursAccumulated || '00:00:00'

      // El servidor ya devuelve el total_hours actualizado con el tiempo acumulado
      // Solo necesitamos calcular los segundos adicionales para mostrar en tiempo real
      const totalAccumulated = this.totalHoursAccumulated || '00:00:00'

      // Parsear el tiempo del servidor (formato HH:MM)
      const parts = totalAccumulated.split(':').map(Number)
      const serverHours = parts[0] || 0
      const serverMinutes = parts[1] || 0

      // Calcular cuántos segundos han pasado desde el último minuto completo
      const startTime = new Date(timestamp * 1000)
      const now = new Date()
      const diffMs = now - startTime

      if (diffMs < 0) {
        return `${totalAccumulated}:00`
      }

      const diffSeconds = Math.floor(diffMs / 1000)
      const currentMinute = Math.floor(diffSeconds / 60)

      // Los segundos adicionales son los segundos dentro del minuto actual
      const additionalSeconds = diffSeconds % 60

      // Si el minuto actual es mayor que los minutos del servidor, usar el minuto actual
      // (esto puede pasar si hay un pequeño retraso en la respuesta del servidor)
      const totalMinutes = Math.max(currentMinute, serverHours * 60 + serverMinutes)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(additionalSeconds).padStart(2, '0')}`
    },
    async loadCurrentJourney() {
      try {
        await this.getCurrentJourneyAction()
      } catch (error) {
        console.error('Error al cargar jornada actual:', error)
      }
    },
    async startJourney() {
      if (this.showScheduleSelector && !this.selectedScheduleId) {
        this.showScheduleModal = true
        return
      }
      await this.doStartJourney()
    },
    async confirmScheduleAndStart() {
      await this.doStartJourney()
    },
    async doStartJourney() {
      try {
        const params = {}
        if (this.selectedScheduleId) params.work_schedule_id = this.selectedScheduleId
        const response = await this.startJourneyAction(params)
        if (response && response.code === 'falta_motivo') {
          this.motiveStartText = ''
          this.showMotiveStartModal = true
        } else {
          await this.getCurrentJourneyAction()
        }
      } catch (error) {
        console.error('Error al iniciar jornada:', error)
      }
    },
    async submitStartMotive() {
      if (!this.motiveStartText.trim()) {
        return
      }
      try {
        const params = { motivo: this.motiveStartText }
        if (this.selectedScheduleId) params.work_schedule_id = this.selectedScheduleId
        const response = await this.startJourneyAction(params)
        // Verificar si fue exitoso (no tiene código de error)
        if (response && !response.code) {
          // Recargar la jornada actual para obtener hour_config_exit
          await this.getCurrentJourneyAction()
          this.showMotiveStartModal = false
          this.motiveStartText = ''
        } else if (response && response.code === 'falta_motivo') {
          // Si aún requiere motivo, mantener el modal abierto
          console.error('El motivo es requerido')
        }
      } catch (error) {
        console.error('Error al iniciar jornada con motivo:', error)
      }
    },
    async completeJourney() {
      try {
        const response = await this.completeJourneyAction({})
        // Verificar si la respuesta contiene código de falta_motivo
        if (response && response.code === 'falta_motivo') {
          this.motiveEndText = ''
          this.showMotiveEndModal = true
        } else {
          // Recargar la jornada actual para obtener hour_fin
          await this.getCurrentJourneyAction()
        }
      } catch (error) {
        console.error('Error al completar jornada:', error)
      }
    },
    async submitEndMotive() {
      if (!this.motiveEndText.trim()) {
        return
      }
      try {
        // Enviar con todos los parámetros más el motivo
        const response = await this.completeJourneyAction({ motivo: this.motiveEndText })
        // Verificar si fue exitoso (no tiene código de error)
        if (response && !response.code) {
          // Recargar la jornada actual para obtener hour_fin
          await this.getCurrentJourneyAction()
          this.showMotiveEndModal = false
          this.motiveEndText = ''
        } else if (response && response.code === 'falta_motivo') {
          // Si aún requiere motivo, mantener el modal abierto
          console.error('El motivo es requerido')
        }
      } catch (error) {
        console.error('Error al completar jornada con motivo:', error)
      }
    },
    async reopenJourney() {
      try {
        await this.startJourneyAction({})
        await this.getCurrentJourneyAction()
      } catch (error) {
        console.error('Error al reabrir jornada:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.journey-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;

  .journey-header {
    background: #fff;
    border-bottom: 2px solid #ff9f43;
    padding: 1rem;

    .header-content {
      display: flex;
      align-items: center;
      gap: 1rem;

      .avatar-wrapper {
        flex-shrink: 0;

        .user-avatar {
          border: 3px solid #ff9f43;
          box-shadow: 0 2px 8px rgba(255, 159, 67, 0.2);
        }
      }

      .greeting-wrapper {
        flex: 1;

        h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #212529;
          line-height: 1.2;
        }

        p {
          font-size: 0.85rem;
          color: #6c757d;
          line-height: 1.3;
        }
      }
    }
  }

  .journey-body {
    padding: 1.5rem;

    .status-content {
      text-align: center;

      .status-icon-inline {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
        color: white;
        font-weight: bold;

        &.success {
          background: #28c76f;
        }

        &.warning {
          background: #ff9f43;
        }

        &.completed {
          background: #6c757d;
        }

        &.completed-success {
          background: #bbf7d0;
          box-shadow: inset 0 0 0 2px #218838;
        }

        &.active-warning {
          background: #fff3cd;
          border: 4px solid #fff3cd;
        }

        &.pending-info {
          background: #bae6fd;
          border: 4px solid #bae6fd;
        }
      }

      h4 {
        font-size: 1.1rem;
        font-weight: 700;
        color: #212529;
      }

      p {
        font-size: 0.9rem;

        .time-badge {
          background: #f0f0f0;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 600;
          color: #212529;
        }
      }

      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 0.75rem;
        margin-bottom: 1.5rem;

        .info-box {
          background: #f8f9fa;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0.75rem;
          text-align: center;

          small {
            display: block;
            font-size: 0.75rem;
            font-weight: 600;
            color: #6c757d;
            margin-bottom: 0.5rem;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          p {
            font-size: 0.95rem;
            margin-bottom: 0;
          }
        }
      }

      .journeys-summary {
        display: grid;
        gap: 0.75rem;

        .journey-summary-box {
          background: #f8f9fa;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0.75rem;

          .summary-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;

            .summary-col {
              text-align: center;
              min-width: 0;

              small {
                display: block;
                font-size: 0.7rem;
                font-weight: 600;
                color: #6c757d;
                margin-bottom: 0.25rem;
                text-transform: uppercase;
                letter-spacing: 0.2px;
              }

              p {
                font-size: 0.9rem;
                margin-bottom: 0;
              }
            }
          }
        }
      }

      .total-hours-box {
        background: #f8f9fa;
        border: 1px solid #ff9f43;
        border-radius: 8px;
        padding: 1rem;
        text-align: center;

        p {
          margin-bottom: 0;

          &.text-muted {
            font-size: 0.75rem;
            font-weight: 600;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          &.h4 {
            color: #ff9f43;
            font-size: 1.3rem;
            margin-bottom: 0;
          }
        }
      }
    }
  }
}

.info-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;

  .info-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1rem;

    .info-item {
      flex: 1;
      text-align: center;

      small {
        display: block;
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        margin-bottom: 0.5rem;
        color: #6c757d;
        min-height: 1rem;
      }

      p {
        font-size: 0.95rem;
      }

      &.border-left {
        border-left: 1px solid #e5e7eb;
        padding-left: 1rem;
      }
    }
  }
}

.hour-display {
  font-size: 1.5rem !important;
  line-height: 1.2;
}

.text-success-strong {
  color: #218838 !important;
}

.text-warning-strong {
  color: #856404 !important;
}

.text-info-strong {
  color: #608fd3 !important;
}

.user-avatar {
  width: 60px !important;
  height: 60px !important;
  min-width: 60px !important;
  min-height: 60px !important;
}

.avatar-initials {
  background-color: #ffe8c9 !important;
  color: #ff9f43 !important;
  font-size: 1.5rem !important;
  font-weight: 700 !important;
}

@media (max-width: 768px) {
  .journey-card {
    .journey-header {
      padding: 0.75rem;

      .header-content {
        gap: 0.75rem;

        .avatar-wrapper .user-avatar {
          width: 50px !important;
          height: 50px !important;
        }

        .greeting-wrapper {
          h3 {
            font-size: 1rem;
          }

          p {
            font-size: 0.8rem;
          }
        }
      }
    }

    .journey-body {
      padding: 1rem;

      .status-content {
        h4 {
          font-size: 1rem;
        }

        .info-grid {
          grid-template-columns: 1fr;
          gap: 0.5rem;

          .info-box {
            padding: 0.6rem;

            small {
              font-size: 0.65rem;
              margin-bottom: 0.3rem;
            }

            p {
              font-size: 0.9rem;
            }
          }
        }

        .journeys-summary {
          gap: 0.5rem;

          .journey-summary-box {
            padding: 0.6rem;

            .summary-row {
              gap: 0.4rem;

              .summary-col {
                small {
                  font-size: 0.65rem;
                  margin-bottom: 0.2rem;
                }

                p {
                  font-size: 0.85rem;
                }
              }
            }
          }
        }

        .total-hours-box {
          padding: 0.75rem;

          p {
            &.text-muted {
              font-size: 0.7rem;
            }

            &.h4 {
              font-size: 1.2rem;
            }
          }
        }
      }
    }
  }

  .info-card {
    .info-content {
      padding: 0.75rem;

      .info-item {
        small {
          font-size: 0.7rem;
        }

        p {
          font-size: 0.85rem;
        }

        &.border-left {
          padding-left: 0.75rem;
        }
      }
    }
  }
}
</style>
